import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { DemoStorageService } from '../../core/demo-storage.service';
import type { ComiteSST, MiembroComite, ReunionComite, ActaReunion, EstadoReunion } from './models/comite-sst.model';

const KEY_COMITES = 'comite_comites';
const KEY_MIEMBROS = 'comite_miembros';
const KEY_REUNIONES = 'comite_reuniones';
const KEY_ACTAS = 'comite_actas';

function demoComites(): ComiteSST[] {
  const now = new Date().toISOString().slice(0, 19).replace('T', ' ');
  const d = (daysAgo: number) =>
    new Date(Date.now() - daysAgo * 24 * 3600 * 1000).toISOString().slice(0, 10);
  return [
    {
      id: '1',
      nombre: 'Comité de Seguridad y Salud en el Trabajo',
      alcance: 'Sede principal',
      vigenteDesde: d(730),
      activo: true,
      createdAt: now,
      updatedAt: now,
    },
  ];
}

function demoMiembros(): MiembroComite[] {
  const now = new Date().toISOString().slice(0, 19).replace('T', ' ');
  const d = (daysAgo: number) =>
    new Date(Date.now() - daysAgo * 24 * 3600 * 1000).toISOString().slice(0, 10);
  return [
    {
      id: '1',
      comiteId: '1',
      nombre: 'María García',
      cargo: 'Gerente General',
      rolEnComite: 'Representante empleadores',
      vigenteDesde: d(730),
      activo: true,
      createdAt: now,
      updatedAt: now,
    },
    {
      id: '2',
      comiteId: '1',
      nombre: 'Carlos López',
      cargo: 'Coordinador SST',
      rolEnComite: 'Secretaría técnica',
      vigenteDesde: d(365),
      activo: true,
      createdAt: now,
      updatedAt: now,
    },
  ];
}

function demoReuniones(): ReunionComite[] {
  const now = new Date().toISOString().slice(0, 19).replace('T', ' ');
  const d = (daysAgo: number) =>
    new Date(Date.now() - daysAgo * 24 * 3600 * 1000).toISOString().slice(0, 10);
  const next = (daysFromNow: number) =>
    new Date(Date.now() + daysFromNow * 24 * 3600 * 1000).toISOString().slice(0, 10);
  return [
    {
      id: '1',
      comiteId: '1',
      fechaProgramada: next(14),
      lugar: 'Sala de juntas',
      convocatoria: 'Reunión ordinaria mensual',
      estado: 'programada',
      createdAt: now,
      updatedAt: now,
    },
    {
      id: '2',
      comiteId: '1',
      fechaProgramada: d(30),
      fechaRealizacion: d(30),
      lugar: 'Sala de juntas',
      convocatoria: 'Reunión ordinaria',
      estado: 'realizada',
      actaId: '1',
      createdAt: now,
      updatedAt: now,
    },
  ];
}

function demoActas(): ActaReunion[] {
  const now = new Date().toISOString().slice(0, 19).replace('T', ' ');
  const next = (days: number) => new Date(Date.now() + days * 24 * 3600 * 1000).toISOString().slice(0, 10);
  return [
    {
      id: '1',
      reunionId: '2',
      contenido: 'Se revisó el plan anual de SST, indicadores del mes y seguimiento a acuerdos anteriores.',
      acuerdos: '1. Actualizar matriz de riesgos en Producción. 2. Programar simulacro de evacuación para próximo mes.',
      responsables: 'Coordinador SST, Brigada evacuación',
      fechaLimiteSeguimiento: next(45),
      firmas: { 'María García': '2025-01-15', 'Carlos López': '2025-01-15' },
      createdAt: now,
      updatedAt: now,
    },
  ];
}

@Injectable({ providedIn: 'root' })
export class ComiteSstService {
  private comites: ComiteSST[] = demoComites();
  private miembros: MiembroComite[] = demoMiembros();
  private reuniones: ReunionComite[] = demoReuniones();
  private actas: ActaReunion[] = demoActas();

  constructor(private demoStorage: DemoStorageService) {}

  async loadFromDemoStorage(): Promise<void> {
    if (!environment.demo) return;
    const [c, m, r, a] = await Promise.all([
      this.demoStorage.get<ComiteSST[]>(KEY_COMITES),
      this.demoStorage.get<MiembroComite[]>(KEY_MIEMBROS),
      this.demoStorage.get<ReunionComite[]>(KEY_REUNIONES),
      this.demoStorage.get<ActaReunion[]>(KEY_ACTAS),
    ]);
    this.comites = Array.isArray(c) ? c : demoComites();
    this.miembros = Array.isArray(m) ? m : demoMiembros();
    this.reuniones = Array.isArray(r) ? r : demoReuniones();
    this.actas = Array.isArray(a) ? a : demoActas();
    await this.persistDemo();
  }

  private persistDemo(): Promise<void> {
    if (!environment.demo) return Promise.resolve();
    return Promise.all([
      this.demoStorage.set(KEY_COMITES, this.comites),
      this.demoStorage.set(KEY_MIEMBROS, this.miembros),
      this.demoStorage.set(KEY_REUNIONES, this.reuniones),
      this.demoStorage.set(KEY_ACTAS, this.actas),
    ]).then(() => {}).catch(() => {});
  }

  getComites(): ComiteSST[] {
    return this.comites.filter((c) => c.activo);
  }

  getComiteById(id: string): ComiteSST | null {
    return this.comites.find((c) => c.id === id) ?? null;
  }

  getMiembros(comiteId: string): MiembroComite[] {
    return this.miembros.filter((m) => m.comiteId === comiteId && m.activo);
  }

  getAllMiembros(comiteId: string): MiembroComite[] {
    return this.miembros.filter((m) => m.comiteId === comiteId);
  }

  getReuniones(comiteId?: string): ReunionComite[] {
    let list = [...this.reuniones];
    if (comiteId) list = list.filter((r) => r.comiteId === comiteId);
    return list.sort((a, b) => b.fechaProgramada.localeCompare(a.fechaProgramada));
  }

  getReunionById(id: string): ReunionComite | null {
    return this.reuniones.find((r) => r.id === id) ?? null;
  }

  getActaByReunionId(reunionId: string): ActaReunion | null {
    return this.actas.find((a) => a.reunionId === reunionId) ?? null;
  }

  getActaById(id: string): ActaReunion | null {
    return this.actas.find((a) => a.id === id) ?? null;
  }

  createReunion(data: Omit<ReunionComite, 'id' | 'createdAt' | 'updatedAt'>): ReunionComite {
    const now = new Date().toISOString().slice(0, 19).replace('T', ' ');
    const nueva: ReunionComite = { ...data, id: 'r' + Date.now(), createdAt: now, updatedAt: now };
    this.reuniones.push(nueva);
    this.persistDemo();
    return { ...nueva };
  }

  updateReunion(id: string, data: Partial<Omit<ReunionComite, 'id' | 'createdAt'>>): ReunionComite | null {
    const r = this.reuniones.find((x) => x.id === id);
    if (!r) return null;
    Object.assign(r, data);
    r.updatedAt = new Date().toISOString().slice(0, 19).replace('T', ' ');
    this.persistDemo();
    return { ...r };
  }

  marcarRealizada(id: string, fechaRealizacion?: string): ReunionComite | null {
    const f = fechaRealizacion || new Date().toISOString().slice(0, 10);
    return this.updateReunion(id, { estado: 'realizada', fechaRealizacion: f });
  }

  createActa(reunionId: string, data: Omit<ActaReunion, 'id' | 'reunionId' | 'createdAt' | 'updatedAt'>): ActaReunion {
    const now = new Date().toISOString().slice(0, 19).replace('T', ' ');
    const nueva: ActaReunion = { ...data, id: 'a' + Date.now(), reunionId, createdAt: now, updatedAt: now };
    this.actas.push(nueva);
    const reunion = this.reuniones.find((r) => r.id === reunionId);
    if (reunion) {
      reunion.actaId = nueva.id;
      reunion.updatedAt = now;
    }
    this.persistDemo();
    return { ...nueva };
  }

  updateActa(id: string, data: Partial<Omit<ActaReunion, 'id' | 'reunionId' | 'createdAt'>>): ActaReunion | null {
    const a = this.actas.find((x) => x.id === id);
    if (!a) return null;
    Object.assign(a, data);
    a.updatedAt = new Date().toISOString().slice(0, 19).replace('T', ' ');
    this.persistDemo();
    return { ...a };
  }

  addFirma(actaId: string, nombreFirmante: string): ActaReunion | null {
    const a = this.actas.find((x) => x.id === actaId);
    if (!a) return null;
    if (!a.firmas) a.firmas = {};
    a.firmas[nombreFirmante] = new Date().toISOString().slice(0, 10);
    a.updatedAt = new Date().toISOString().slice(0, 19).replace('T', ' ');
    this.persistDemo();
    return { ...a };
  }

  createMiembro(data: Omit<MiembroComite, 'id' | 'createdAt' | 'updatedAt'>): MiembroComite {
    const now = new Date().toISOString().slice(0, 19).replace('T', ' ');
    const nuevo: MiembroComite = { ...data, id: 'm' + Date.now(), createdAt: now, updatedAt: now };
    this.miembros.push(nuevo);
    this.persistDemo();
    return { ...nuevo };
  }

  updateMiembro(id: string, data: Partial<Omit<MiembroComite, 'id' | 'createdAt'>>): MiembroComite | null {
    const m = this.miembros.find((x) => x.id === id);
    if (!m) return null;
    Object.assign(m, data);
    m.updatedAt = new Date().toISOString().slice(0, 19).replace('T', ' ');
    this.persistDemo();
    return { ...m };
  }

  /** Historial: actas con acuerdos para seguimiento. */
  getHistorialDecisiones(comiteId?: string): { acta: ActaReunion; reunion: ReunionComite; comite: ComiteSST | null }[] {
    const result: { acta: ActaReunion; reunion: ReunionComite; comite: ComiteSST | null }[] = [];
    for (const acta of this.actas) {
      const reunion = this.getReunionById(acta.reunionId);
      if (!reunion || (comiteId && reunion.comiteId !== comiteId)) continue;
      const comite = this.getComiteById(reunion.comiteId);
      result.push({ acta, reunion, comite });
    }
    result.sort((a, b) => b.reunion.fechaProgramada.localeCompare(a.reunion.fechaProgramada));
    return result;
  }
}
